import { useLocation } from 'react-router-dom'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useNewIssueDialogStore } from '@/routes/projectStages/issues/lib/stores/useNewIssueDialogStore.ts'
import { useLicenceAllowsCreate } from '@/lib/licence/useStageLicence.ts'
import { useUserStore } from '@/store/user.ts'
import createIri from '@/lib/helpers/createIri.ts'
import { matchGlobalNewIssueRoute } from '@/routes/projectStages/lib/newIssueEntryRoutes.ts'
import {
  isCreatableIssueType,
  readLastUsedIssueType,
  resolveGlobalIssueTypeIri,
} from '@/routes/projectStages/issues/lib/lastUsedIssueType.ts'

/**
 * The stage-wide "Report" entry point (mobile FAB off the issues list,
 * desktop sidebar button). `available` is false off the routes that offer it
 * (see matchGlobalNewIssueRoute), while the stage store still holds another
 * stage, when the licence refuses new content, or when the user may create no
 * issue type here. `open` picks the default type and opens the New Issue
 * dialog in global mode, where the type can be changed in the form.
 *
 * Setting the global current type is safe only because these routes do not
 * derive anything from it — the same thing the dashboard card trigger relies
 * on today.
 */
export function useGlobalNewIssueEntry() {
  const { pathname } = useLocation()
  const routeStageId = matchGlobalNewIssueRoute(pathname)
  const currProjectStage = useProjectStageStore(s => s.currProjectStage)
  const issueTypes = useProjectStageStore(s => s.issueTypes)
  const currIssueType = useProjectStageStore(s => s.currIssueType)
  const setCurrIssueType = useProjectStageStore(s => s.setCurrIssueType)
  const openGlobalNewIssue = useNewIssueDialogStore(s => s.openGlobalNewIssue)
  const userId = useUserStore(s => s.user?.id)
  const licenceAllowsCreate = useLicenceAllowsCreate()

  // Right after a stage switch the store still holds the previous stage's
  // issue types; offering them would create an issue in the wrong stage.
  const storeHoldsRouteStage =
    routeStageId !== null && !!currProjectStage && String(currProjectStage.id) === routeStageId

  const available =
    storeHoldsRouteStage && licenceAllowsCreate && issueTypes.some(isCreatableIssueType)

  function open() {
    if (!available || routeStageId === null) return
    const issueTypeIri = resolveGlobalIssueTypeIri({
      issueTypes,
      lastUsedIri: readLastUsedIssueType(userId, routeStageId),
      currIssueTypeIri: currIssueType ? createIri(`issue_types`, currIssueType.id) : null,
    })
    if (!issueTypeIri) return
    setCurrIssueType(issueTypeIri)
    openGlobalNewIssue()
  }

  return { available, open }
}
